import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client';
import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { getApiUrl } from 'utils/env';
import { authErrorLink, getOnboardingToken, getApolloDevtoolsOptions } from '@blocs.auth';
import { captureExceptionEvent } from '@blocs.observability';
import { initializeState, resolvers } from './ApolloLinkState';

let apolloClient = null;

const authLink = new ApolloLink((operation, forward) => {
  const accessToken = getOnboardingToken();

  // add the authorization to the headers
  operation.setContext(({ headers }) => ({
    headers: {
      ...headers,
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
  }));

  return forward(operation);
});

async function create(initialState) {
  const apiUrl = getApiUrl();
  const httpLink = createUploadLink({ uri: apiUrl, credentials: 'same-origin' });

  const cache = new InMemoryCache().restore(initialState || {});

  await persistCache({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
  });

  const errorLink = authErrorLink(
    (error) => {
      // Handle any error which is not 401
      const errorToReport =
        error.networkError instanceof Error
          ? error.networkError
          : new Error(error.graphQLErrors?.[0]?.message || 'Apollo error');
      captureExceptionEvent(errorToReport);
      console.error('apollo error link', { error });
    },
    '/login',
    true
  );

  const client = new ApolloClient({
    link: ApolloLink.from([errorLink, authLink.concat(httpLink)]),
    resolvers,
    cache,
    name: 'web',
    version: 'onboarding',
    devtools: getApolloDevtoolsOptions(),
  });
  initializeState(cache);

  return client;
}

export default async function initApollo(initialState = {}) {
  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = await create(initialState);
  }

  return apolloClient;
}
